import * as React from 'react';
import clsx from 'clsx';
import { useSearchParams } from 'react-router-dom';
import { InvoicesHeader } from './InvoicesHeader';
import { InvoicesList } from './InvoicesList';
import { CreditsView } from './credits/CreditsView';
import { useInvoiceDownload } from '../../hooks/useInvoiceDownload';
import { useInvoices } from '../../hooks/useInvoices';

import invoiceIcon from '@/assets/images/svg/invoice_icon.svg';
import creditsIcon from '@/assets/images/svg/credits_icon.svg';

type PaymentSubTab = 'invoices' | 'plans';

const TABS: { key: PaymentSubTab; label: string; icon: string }[] = [
  { key: 'invoices', label: 'Invoices', icon: invoiceIcon },
  { key: 'plans', label: 'Plans & credits', icon: creditsIcon },
];

export const PaymentsTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { handleDownload, handleDownloadAll } = useInvoiceDownload();
  const { invoices, isLoading, isError, periodLabel, setPeriod, refetch } = useInvoices();

  const requestedSubTab = searchParams.get('sub');
  const initialSubTab: PaymentSubTab =
    requestedSubTab === 'plans' || requestedSubTab === 'credits' ? 'plans' : 'invoices';
  const [activeTab, setActiveTab] = React.useState<PaymentSubTab>(initialSubTab);

  React.useEffect(() => {
    const sub = searchParams.get('sub');
    if (sub === 'invoices') {
      setActiveTab('invoices');
    } else if (sub === 'plans' || sub === 'credits') {
      setActiveTab('plans');
    }
  }, [searchParams]);

  const handleSubTabChange = (sub: PaymentSubTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('sub', sub);
    if (sub === 'invoices') next.delete('focus');
    setSearchParams(next);
  };

  return (
    <div className='animate-slide-in flex w-full flex-col pt-2'>
      <div className='flex w-full flex-col'>
        {/* Tab Navigation */}
        <div className='mb-8 flex items-center gap-8 border-b border-gray-200 pb-1'>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleSubTabChange(tab.key)}
              className={clsx(
                'flex items-center gap-2 pb-3 text-16 font-medium leading-27 transition-all',
                activeTab === tab.key
                  ? 'border-b-2 border-brand-800 font-bold text-brand-800'
                  : 'border-b-2 border-transparent text-gray-500 hover:text-brand-800'
              )}
            >
              <img
                src={tab.icon}
                alt=''
                className={clsx(
                  'size-5 transition-opacity',
                  activeTab === tab.key ? 'opacity-100' : 'opacity-60'
                )}
              />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className='animate-slide-in'>
          {activeTab === 'invoices' && (
            <>
              <InvoicesHeader
                periodLabel={periodLabel}
                onPeriodChange={(year, month) => setPeriod({ year, month })}
                onDownloadAll={() => handleDownloadAll(invoices)}
                canDownload={invoices.some((invoice) => invoice.documentAvailable)}
                hideTitle={true}
              />
              {isLoading ? (
                <div className='py-8 text-center font-sans text-gray-500'>Loading invoices...</div>
              ) : isError ? (
                <div className='rounded-16 border border-red-150 bg-white p-6' role='alert'>
                  <p className='font-sans text-16 font-bold text-brand-800'>
                    Invoices did not load
                  </p>
                  <p className='mt-1 font-sans text-14 text-gray-650'>
                    Your billing records are unchanged. Check your connection and try again.
                  </p>
                  <button
                    type='button'
                    onClick={refetch}
                    className='mt-4 font-sans text-14 font-bold text-brand-800 underline'
                  >
                    Retry loading
                  </button>
                </div>
              ) : invoices.length > 0 ? (
                <InvoicesList invoices={invoices} onDownload={handleDownload} />
              ) : (
                <div className='py-8 text-center font-sans text-gray-500'>
                  No invoices available.
                </div>
              )}
            </>
          )}

          {activeTab === 'plans' && <CreditsView />}
        </div>
      </div>
    </div>
  );
};
